<template>
  <div style="position: relative;">
    <div>
      <LanguagesCard
        v-for="(language, index) of peParty.learned_languages"
        :key="index"
        :item="language"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <LanguagesCard
        v-for="item of partySurveyCardsByIdentifiers('languages-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Language"
      :has-new-section="peLanguagesCardHasNewSection"
      @on-add="peLanguagesCardAddSection"
    />

    <IncomeCard
      class="mb-4"
      :item="peParty"
    />

    <div v-if="peSurveysValidationActive">
      <IncomeCard
        v-for="item of partySurveyCardsByIdentifiers('income-card')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <div>
      <ImmigrationProceedingsCard
        v-for="immigration_proceeding of peSortedCharacteristic('immigration_proceedings')"
        :key="immigration_proceeding.id"
        :item="immigration_proceeding"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <ImmigrationProceedingsCard
        v-for="item of partySurveyCardsByIdentifiers('immigration-proceedings-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Immigration Proceeding"
      :has-new-section="peImmigrationProceedingsCardHasNewSection"
      @on-add="peImmigrationProceedingsCardAddSection"
    />

    <div>
      <ImmigrationBenefitsCard
        v-for="immigration_benefit of peSortedCharacteristic('immigration_benefits')"
        :key="immigration_benefit.id"
        :item="immigration_benefit"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <ImmigrationBenefitsCard
        v-for="item of partySurveyCardsByIdentifiers('immigration-benefits-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Immigration Benefit"
      :has-new-section="peImmigrationBenefitsCardHasNewSection"
      @on-add="peImmigrationBenefitsCardAddSection"
    />

    <v-overlay
      absolute
      :value="!canView"
      :class="{'party-overlay': !canView}"
      class="align-start"
    >
      <v-row
        align="start"
        justify="center"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <h2>You do not have permissions to view this section.</h2>
        </v-col>
      </v-row>
    </v-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import Actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { USER_NAMESPACE } from '@/store/user'

import ImmigrationBenefitsCard from './SectionCards/BackgroundCards/ImmigrationBenefitsCard'
import ImmigrationProceedingsCard from './SectionCards/BackgroundCards/ImmigrationProceedingsCard'
import IncomeCard from './SectionCards/BackgroundCards/IncomeCard'
import LanguagesCard from './SectionCards/BackgroundCards/LanguagesCard'
import SectionAdderLine from './SectionTools/SectionAdderLine'

export default {
  name: 'PEBackground',

  components: {
    ImmigrationBenefitsCard,
    ImmigrationProceedingsCard,
    IncomeCard,
    LanguagesCard,
    SectionAdderLine,
  },

  beforeRouteLeave (_, __, next) {
    if (this.peAnyUnlockedSections) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!')
      if (answer) {
        this.peLockPartySections()
        next()
      } else next(false)
    } else {
      this.peLockPartySections()
      next()
    }
  },

  props: {
    id: {
      type: String || Number,
      required: true,
    },
  },

  setup () {
    return { ...alertHandler() }
  },

  data () {
    return {
      canView: true,
    }
  },

  computed: {
    userCan () {
      return {
        viewSection: this.can(Actions.Platform.PartyEdit.Sections.Background.View),
      }
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peAnyUnlockedSections',
      'peImmigrationBenefitsCardHasNewSection',
      'peImmigrationProceedingsCardHasNewSection',
      'peLanguagesCardHasNewSection',
      'peParty',
      'peSortedCharacteristic',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'partySurveyCardsByIdentifiers',
      'peSurveysValidationActive',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  created () {
    if (!this.userCan.viewSection) {
      this.canView = false
      this.displayAlert('error', 'You do not have permissions to view this section.')
      return
    }

    this.peGetSelectedPartySection({ id: this.id, section: 'background' }).
      catch(e => {
        if (e.status === 403) this.$router.push({ name: 'resourceAccessDenied', query: { party_id: this.id } })
        else if (e.status === 404) this.$router.push({ name: 'resourceNotFound' })
        else this.displayAlert('error', `An unexpected error occurred: ${e.message}`)
      })
  },

  methods: {
    ...mapActions(PARTY_NAMESPACE, [
      'peGetSelectedPartySection',
      'peImmigrationBenefitsCardAddSection',
      'peImmigrationProceedingsCardAddSection',
      'peLanguagesCardAddSection',
      'peLockPartySections',
    ]),
  },
}
</script>

<style scoped>
.party-overlay {
  margin-top: -75px;
  padding-top: 50vh;
  z-index: 10;
  backdrop-filter: blur(6px);
}
</style>
